// Analytics (docs/ARCHITECTURE-ANALYTICS.md): one summary endpoint for a clinic, a strategy or a
// piece, so tiles, tables and charts always agree. Owner and manager only until the owner opens
// a door in Admin (GET /analytics/me decides the menu).
import { api } from './api';

export type Scope = 'clinic' | 'strategy' | 'creative';
export type Show = 'both' | 'organic' | 'paid';

export interface OrganicBlock {
  posts: number; views: number; reach: number; likes: number; comments: number; saves: number; shares: number;
  engagement_rate: number | null; save_share_rate: number | null; avg_watch_s: number | null; skip_rate: number | null;
  post_follows: number; profile_visits: number; crm_leads?: number; revenue?: number;
}
export interface PaidBlock {
  spend?: number; currency: string | null; mixed_currency: boolean; impressions: number; reach_sum: number; clicks: number;
  link_clicks: number; ctr: number | null; cpm?: number | null; cpc?: number | null; hook_rate: number | null; thruplays: number;
  platform_results: Record<string, number>; cost_per_platform_result: Record<string, number>;
  crm_leads: number; revenue: number; cost_per_lead?: number | null; roas?: number | null;
}
export interface PieceRow {
  id: string; code: string; title: string; funnel_stage: string | null; type: string; stage: string;
  organic: { views: number | null; reach: number | null; engagement_rate: number | null; post_follows: number | null };
  paid: { spend?: number | null; ctr: number | null; crm_leads: number; cost_per_lead?: number | null };
  result: { leads: number; revenue: number; roas?: number | null };
}
export interface Summary {
  scope: Scope; id: string; title: string | null; since: string; until: string; provisional_from: string;
  results: { revenue: number; leads: number; currency: string | null; attributed_pct: number | null;
    split: Record<'paid' | 'organic' | 'none', { leads: number; revenue: number }> };
  organic: { total: OrganicBlock; by_platform: Record<string, OrganicBlock>; followers_gained: Record<string, number> | null } | null;
  paid: { total: PaidBlock; by_platform: Record<string, PaidBlock> } | null;
  pieces: PieceRow[];
  sources: { kind: string; status: string; last_ok_at: string | null }[];
  crm_received_at: string | null;
  plan?: Record<string, { planned: number; posted: number; in_production: number }>;
}
export interface SummaryParams {
  scope: Scope; id: string; from?: string; to?: string; platforms?: string[]; show?: Show; stage?: string; type?: string;
}

export const akeys = {
  me: ['analytics', 'me'] as const,
  access: ['analytics', 'access'] as const,
  summary: (p: SummaryParams) => ['analytics', 'summary', p] as const,
  sources: (clientId: string) => ['analytics', 'sources', clientId] as const,
  ads: (creativeId: string) => ['analytics', 'ads', creativeId] as const,
  unlinked: (clientId: string) => ['analytics', 'unlinked', clientId] as const,
};

export async function fetchAnalyticsMe(): Promise<{ can_view: boolean; audience: string; clinics: { id: string; name: string }[] }> {
  return (await api.get('/analytics/me')).data;
}
export async function fetchAnalyticsAccess(): Promise<{ staff: boolean; portal: boolean }> {
  return (await api.get('/analytics/access')).data;
}
export async function saveAnalyticsAccess(body: { staff: boolean; portal: boolean }) {
  return (await api.put('/analytics/access', body)).data as { staff: boolean; portal: boolean };
}
export async function fetchSummary(p: SummaryParams): Promise<Summary> {
  const params: Record<string, string> = { scope: p.scope, id: p.id, show: p.show ?? 'both' };
  if (p.from) params.from = p.from;
  if (p.to) params.to = p.to;
  if (p.platforms?.length) params.platforms = p.platforms.join(',');
  if (p.stage) params.stage = p.stage;
  if (p.type) params.type = p.type;
  return (await api.get('/analytics/summary', { params })).data;
}
export async function fetchSources(clientId: string) {
  return (await api.get('/analytics/sources', { params: { client_id: clientId } })).data as
    { kind: string; external_id: string; label: string | null; status: string; currency: string | null; last_ok_at: string | null; last_error: string | null }[];
}
export async function fetchCreativeAds(creativeId: string) {
  return (await api.get(`/analytics/creative/${creativeId}/ads`)).data as
    { platform: string; ad_id: string; ad_name: string | null; campaign_name: string | null; linked_by: string }[];
}
export async function fetchUnlinkedAds(clientId: string) {
  return (await api.get('/analytics/unlinked-ads', { params: { client_id: clientId } })).data as
    { platform: string; ad_id: string; ad_name: string | null; campaign_name: string | null; spend: number }[];
}
export async function linkAd(creativeId: string, platform: string, adId: string) {
  return (await api.post(`/analytics/creative/${creativeId}/ads`, { platform, ad_id: adId })).data;
}
export async function unlinkAd(creativeId: string, platform: string, adId: string) {
  return (await api.delete(`/analytics/creative/${creativeId}/ads/${platform}/${adId}`)).data;
}
export async function tiktokLink(clientId: string) {
  return (await api.post('/analytics/tiktok/link', { client_id: clientId })).data as { url: string; expires_days: number; clinic: string };
}
export async function syncNow(days = 7) {
  return (await api.post('/analytics/sync', null, { params: { days } })).data;
}

// ---- formatting: one place, so every page writes numbers the same way
export const fmtInt = (n: number | null | undefined) => (n == null ? '-' : Math.round(n).toLocaleString('en-US'));
export const fmtMoney = (n: number | null | undefined, cur?: string | null) =>
  n == null ? '-' : `${cur ?? 'AED'} ${Math.round(n).toLocaleString('en-US')}`;
export const fmtPct = (n: number | null | undefined) => (n == null ? '-' : `${n.toFixed(n < 10 ? 1 : 0)}%`);
export const fmtX = (n: number | null | undefined) => (n == null ? '-' : `${n.toFixed(1)}×`);
export const RESULT_LABEL: Record<string, string> = {
  messaging_conversation_started: 'conversations', lead: 'form leads', website_lead: 'website leads', conversion: 'conversions',
};
export const PLATFORM_LABEL: Record<string, string> = {
  instagram: 'Instagram', facebook: 'Facebook', tiktok: 'TikTok', meta: 'Meta (Instagram + Facebook)', snap: 'Snapchat', snapchat: 'Snapchat',
};
